import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { roomService } from '../services/roomService';
import toast from 'react-hot-toast';

const { FiLogIn, FiLoader, FiEye, FiEyeOff } = FiIcons;

const RoomJoin = ({ onJoinRoom }) => {
  const [formData, setFormData] = useState({
    roomName: '',
    password: ''
  });
  const [isJoining, setIsJoining] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.roomName.trim() || !formData.password.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsJoining(true);
    try {
      const result = await roomService.verifyRoom(formData.roomName.trim(), formData.password.trim());
      if (result.success) {
        onJoinRoom(formData.roomName.trim(), formData.password.trim());
        toast.success('Joined room successfully!');
      } else {
        toast.error(result.error || 'Failed to join room');
      }
    } catch (error) {
      toast.error('Failed to join room');
    } finally {
      setIsJoining(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <motion.div
      className="bg-white rounded-2xl shadow-xl p-8 max-w-md mx-auto"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <SafeIcon icon={FiLogIn} className="w-8 h-8 text-primary-600" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Join Room</h3>
        <p className="text-gray-600">
          Enter room credentials to start sharing
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="roomName" className="block text-sm font-medium text-gray-700 mb-2">
            Room Name
          </label>
          <input
            type="text"
            id="roomName"
            name="roomName"
            value={formData.roomName}
            onChange={handleInputChange}
            placeholder="Enter room name"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
            disabled={isJoining}
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
            Room Password
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder="Enter room password"
              className="w-full p-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
              disabled={isJoining}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <SafeIcon icon={showPassword ? FiEyeOff : FiEye} className="w-5 h-5" />
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={isJoining || !formData.roomName.trim() || !formData.password.trim()}
          className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2"
        >
          {isJoining ? (
            <>
              <SafeIcon icon={FiLoader} className="w-5 h-5 animate-spin" />
              Joining...
            </>
          ) : (
            <>
              <SafeIcon icon={FiLogIn} className="w-5 h-5" />
              Join Room
            </>
          )}
        </button>
      </form>

      <div className="mt-6 p-4 bg-green-50 rounded-lg">
        <h4 className="font-medium text-green-900 mb-2">Tip:</h4>
        <p className="text-sm text-green-800">
          Make sure you have the correct room name and password from the room creator.
        </p>
      </div>
    </motion.div>
  );
};

export default RoomJoin;